import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Clock, CalendarClock, MapPin, Hash, X, Navigation, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { StatoBadge } from "@/components/Badges";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CAT_COLOR } from "@/lib/constants";
import { interval, fmtDate } from "@/lib/time";
import { cn } from "@/lib/utils";

const pad = (n) => String(n).padStart(2, "0");
const dateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mapsUrl = (t) => {
  const q = [t.address, t.comune].filter(Boolean).join(", ");
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
};

export default function MobileAgenda() {
  const nav = useNavigate();
  const [tickets, setTickets] = useState(null);
  const [dates, setDates] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("agenda_dates") || "[]");
      if (Array.isArray(saved) && saved.length) return saved.map((s) => new Date(s + "T00:00:00"));
    } catch { /* ignore */ }
    return [new Date()];
  });
  const [open, setOpen] = useState(false);

  const load = () => api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);
  useLiveRefresh(load);

  useEffect(() => {
    sessionStorage.setItem("agenda_dates", JSON.stringify(dates.map(dateStr)));
  }, [dates]);

  const selectedKeys = dates.map(dateStr).sort();
  const rowsByDate = selectedKeys.map((key) => ({
    key,
    items: (tickets || [])
      .filter((t) => t.intervention_date === key)
      .sort((a, b) => (a.start_time || a.appointment_time || "").localeCompare(b.start_time || b.appointment_time || "")),
  }));
  const totalCount = rowsByDate.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-navy">Agenda</h1>
          <p className="text-sm text-muted-foreground">I tuoi interventi programmati</p>
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="agenda-date-picker" className="shrink-0 gap-1.5">
              <CalendarDays size={16} />
              {dates.length === 1 ? fmtDate(dateStr(dates[0])) : `${dates.length} date`}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0">
            <Calendar mode="multiple" selected={dates} onSelect={(d) => setDates(d && d.length ? d : [new Date()])} data-testid="agenda-calendar" />
            <div className="flex items-center justify-between gap-2 border-t border-border p-2">
              <Button variant="ghost" size="sm" onClick={() => setDates([new Date()])} data-testid="agenda-today">Oggi</Button>
              <span className="text-xs text-muted-foreground">{dates.length} selezionate</span>
              <Button size="sm" onClick={() => setOpen(false)} data-testid="agenda-date-done">Fatto</Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {dates.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="agenda-selected-dates">
          {selectedKeys.map((k) => (
            <span key={k} className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
              {fmtDate(k)}
              <button onClick={() => setDates((prev) => prev.filter((d) => dateStr(d) !== k))} data-testid={`agenda-date-remove-${k}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {tickets === null ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : totalCount === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">Nessun intervento nelle date selezionate</div>
      ) : (
        <div className="space-y-5">
          {rowsByDate.map((g) => (
            g.items.length === 0 ? null : (
              <div key={g.key} className="space-y-3">
                {dates.length > 1 && (
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{fmtDate(g.key)}</p>
                )}
                {g.items.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => nav(`/m/tickets/${t.id}`)}
                    data-testid={`agenda-ticket-${t.code}`}
                    className="flex w-full items-stretch gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors active:bg-secondary/50"
                  >
                    <div className="flex w-16 shrink-0 flex-col items-center justify-center rounded-lg bg-primary/10 py-2 text-primary">
                      <CalendarClock size={16} />
                      <span className="mt-1 text-sm font-extrabold">{t.start_time || t.appointment_time || "—"}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-primary">{t.code}</span>
                        <StatoBadge stato={t.status} />
                        {t.overdue && <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">In ritardo</span>}
                        {t.anticipo && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">{t.anticipo}</span>}
                      </div>
                      <p className="mt-0.5 truncate text-base font-bold text-navy">{t.client_name}</p>
                      {t.anticipo && (
                        <p className={cn("mt-1 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-extrabold uppercase", t.anticipo === "anticipato" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800")} data-testid={`agenda-anticipo-note-${t.code}`}>
                          <AlertTriangle size={13} className="shrink-0" />Intervento {t.anticipo} · appuntamento cliente {fmtDate(t.scheduled_date)}
                        </p>
                      )}
                      <div className="mt-1 space-y-0.5 text-xs">
                        <p className="inline-flex items-center gap-1.5 text-muted-foreground">
                          <span className="h-2 w-2 rounded-full" style={{ background: CAT_COLOR[t.category] || "#64748b" }} />{t.category}
                        </p>
                        <p className="flex items-center gap-1.5 font-bold text-primary" data-testid={`agenda-intervento-${t.code}`}>
                          <CalendarClock size={12} />Intervento {fmtDate(t.intervention_date)}{t.start_time ? ` · ${interval(t)}` : ""}
                        </p>
                        {t.scheduled_date && <p className="flex items-center gap-1.5 text-muted-foreground" data-testid={`agenda-appuntamento-${t.code}`}><Clock size={12} />Appuntamento cliente {fmtDate(t.scheduled_date)} {t.appointment_time || ""}</p>}
                        {t.id_olo && <p className="flex items-center gap-1.5 text-muted-foreground"><Hash size={12} />OLO {t.id_olo}</p>}
                        {t.id_risorsa && <p className="flex items-center gap-1.5 text-muted-foreground"><Hash size={12} />Risorsa {t.id_risorsa}</p>}
                        {t.comune && <p className="flex items-center gap-1.5 text-muted-foreground"><MapPin size={12} />{t.comune}</p>}
                      </div>
                    </div>
                    {mapsUrl(t) && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); window.open(mapsUrl(t), "_blank", "noopener"); }}
                        data-testid={`agenda-map-${t.code}`}
                        className="flex w-11 shrink-0 self-center flex-col items-center justify-center gap-1 rounded-lg border border-primary/30 bg-primary/5 py-3 text-primary active:bg-primary/15"
                        aria-label="Apri in mappa"
                      >
                        <Navigation size={16} />
                        <span className="text-[9px] font-bold uppercase">Mappa</span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
